import { Button, DialogDescription } from '@dfh-corp-sites/ui'
import { ChevronRight } from '@material-ui/icons'
import React, { useMemo } from 'react'

import { SimpleProductDetailsFragment } from '../../../graphql/magento'
import { Maybe, Money } from '../../../graphql/search'
import {
  isSanityImageWithAssetId,
  SanityProductImages,
  SanityProductImagesHierarchy,
} from '../../../lib/products/images'
import { ProductModalData } from '../../../lib/products/modal/types'
import Link from '../../global/Link'
import ItemImagesImport from '../details/images/ItemImages'
import Heading from './Heading'
import { modalImagePreviewSourceFactory, modalImageSourceFactory } from './sources'

export interface ProductModalContentProps {
  modalData: ProductModalData
  selectedVariant?: SimpleProductDetailsFragment | null
  variantRetailPrice?: Maybe<Money> | undefined
}

export const ProductModalContent: React.FC<ProductModalContentProps> = ({
  children,
  modalData,
  selectedVariant,
  variantRetailPrice,
}) => {
  const { item, productDetails } = modalData

  // collect product images hierarchy from Sanity product details
  const sanityImages = useMemo(
    (): SanityProductImagesHierarchy => ({
      parent: {
        mainImage:
          productDetails && isSanityImageWithAssetId(productDetails.image)
            ? {
                ...productDetails.image,
                // TODO use `alt` from Sanity when parent product schema updated
                alt: '',
              }
            : undefined,
      },
      children: (productDetails?.products ?? []).reduce((acc, product) => {
        const sku = product?.sku
        if (!sku) {
          return acc
        }

        acc.set(sku, {
          mainImage: isSanityImageWithAssetId(product.mainImage) ? product.mainImage : undefined,
          otherImages: (product.otherImages ?? []).filter(isSanityImageWithAssetId),
        })
        return acc
      }, new Map<string, SanityProductImages>()),
    }),
    [productDetails],
  )

  return (
    <div className="flex h-[80vh] max-h-[722px] sm:max-h-[587px] rounded-[10px] overflow-hidden">
      <section className="flex-1 flex flex-col divide-y divide-brand-rule-grey sm:border-r sm:border-brand-rule-grey overflow-y-auto">
        <Heading
          item={item}
          selectedVariant={selectedVariant}
          variantRetailPrice={variantRetailPrice}
        />
        <div className="sm:hidden">
          <ItemImagesImport
            className="flex gap-5"
            previewClassName="flex-col"
            imageSourceFactory={modalImageSourceFactory}
            imagePreviewSourceFactory={modalImagePreviewSourceFactory}
            item={item}
            selectedVariant={selectedVariant}
            sanityImages={sanityImages}
          />
        </div>
        <div className="px-2.5 md:px-5 py-5 xl:py-6">
          {productDetails?.tagline && (
            <h3 className="font-medium mb-5">{productDetails?.tagline}</h3>
          )}
          {productDetails?.shortDescription && (
            <DialogDescription>{productDetails.shortDescription}</DialogDescription>
          )}
        </div>
        {children}
      </section>

      <section className="hidden sm:flex sm:w-1/2 flex-col h-full">
        <div className="flex-1 overflow-y-auto">
          <ItemImagesImport
            previewClassName="justify-end"
            imageSourceFactory={modalImageSourceFactory}
            imagePreviewSourceFactory={modalImagePreviewSourceFactory}
            item={item}
            selectedVariant={selectedVariant}
            sanityImages={sanityImages}
          />
        </div>

        <div className="py-5 text-center border-t border-brand-rule-grey">
          <Button className="m-auto" variant="text" asChild>
            <Link to={`/products/${item.url_key}`}>
              See full product details <ChevronRight />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  )
}
